"use client";

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { CalendarDays, Car, ClipboardList, Gauge, Loader2, UserRound } from "lucide-react";

import { AppDialog } from "@/components/ui/app-dialog";
import { formatVehiclePlate } from "@/lib/formatters";
import { apiClient } from "@/lib/api-client";
import type { Vehicle } from "@/types/vehicle";
import type { WorkOrder } from "@/types/work-order";

type PaginatedWorkOrders = {
  data: WorkOrder[];
  total: number;
  page: number;
  limit: number;
};

type VehicleDetailsDialogProps = {
  vehicleId: string | null;
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  backLabel?: string;
  onOpenClient?: (clientId: string) => void;
  onOpenWorkOrder?: (workOrderId: string) => void;
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export function VehicleDetailsDialog({
  vehicleId,
  open,
  onClose,
  onBack,
  backLabel = "Voltar",
  onOpenClient,
  onOpenWorkOrder,
}: VehicleDetailsDialogProps) {
  const vehicleQuery = useQuery({
    queryKey: ["vehicle", vehicleId],
    enabled: open && Boolean(vehicleId),
    queryFn: async () => {
      const response = await apiClient.get<Vehicle>(`/veiculos/${vehicleId}`);
      return response.data;
    },
  });

  const workOrdersQuery = useQuery({
    queryKey: ["work-orders", "vehicle-history", vehicleId],
    enabled: open && Boolean(vehicleId),
    queryFn: async () => {
      const response = await apiClient.get<PaginatedWorkOrders>(
        `/ordens-servico?vehicleId=${vehicleId}&page=1&limit=25`,
      );
      return response.data;
    },
  });

  const vehicle = vehicleQuery.data;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={vehicle ? `${vehicle.brand} ${vehicle.model}` : "Detalhes do veículo"}
      description={vehicle ? `Placa ${formatVehiclePlate(vehicle.plate)}` : "Dados e histórico do veículo"}
      size="xl"
      mobileFullscreen
      actions={
        <>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              {backLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Fechar
          </button>
        </>
      }
    >
      {vehicleQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando veículo...
        </div>
      ) : vehicleQuery.isError ? (
        <ErrorState message="Não foi possível carregar os dados deste veículo." onRetry={() => vehicleQuery.refetch()} />
      ) : vehicle ? (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailCard icon={<Car className="h-4 w-4" />} label="Veículo">
              <p>{formatVehiclePlate(vehicle.plate)} · {vehicle.brand} {vehicle.model}</p>
              <p className="mt-0.5 text-xs font-normal text-slate-500">{vehicle.yearModel ?? vehicle.year}/{vehicle.year}</p>
            </DetailCard>
            <DetailCard icon={<Gauge className="h-4 w-4" />} label="Quilometragem">
              {vehicle.mileage.toLocaleString("pt-BR")} km
            </DetailCard>
            <DetailCard icon={<UserRound className="h-4 w-4" />} label="Cliente">
              {onOpenClient ? (
                <button
                  type="button"
                  onClick={() => onOpenClient(vehicle.client.id)}
                  className="text-left font-semibold text-slate-800 underline-offset-2 transition hover:text-[#E8441A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]"
                  aria-label={`Abrir cliente ${vehicle.client.name}`}
                >
                  {vehicle.client.name}
                </button>
              ) : vehicle.client.name}
            </DetailCard>
            <DetailCard icon={<ClipboardList className="h-4 w-4" />} label="Dados adicionais">
              {vehicle.color ?? "Cor não informada"}{vehicle.engine ? ` · ${vehicle.engine}` : ""}
            </DetailCard>
          </div>

          {vehicle.notes ? (
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-800">Observações</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">{vehicle.notes}</p>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Ordens de serviço</h3>
                <p className="text-xs text-slate-500">Histórico deste veículo</p>
              </div>
              {workOrdersQuery.data ? <span className="text-xs text-slate-500">{workOrdersQuery.data.total} no total</span> : null}
            </div>
            {workOrdersQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Carregando histórico...</div>
            ) : workOrdersQuery.isError ? (
              <div className="p-4"><ErrorState message="Não foi possível carregar as OS deste veículo." onRetry={() => workOrdersQuery.refetch()} /></div>
            ) : workOrdersQuery.data?.data.length ? (
              <div className="divide-y divide-slate-100">
                {workOrdersQuery.data.data.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => onOpenWorkOrder?.(order.id)}
                    disabled={!onOpenWorkOrder}
                    className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A] focus-visible:ring-inset"
                    aria-label={onOpenWorkOrder ? `Abrir ordem de serviço ${order.number}` : undefined}
                  >
                    <span className="min-w-0"><span className="flex items-center gap-2 font-semibold text-slate-900"><ClipboardList className="h-4 w-4 shrink-0 text-[#E8441A]" />{order.number}</span><span className="mt-1 flex items-center gap-1 text-xs text-slate-500"><CalendarDays className="h-3.5 w-3.5" />{new Date(order.createdAt).toLocaleDateString("pt-BR")}</span></span>
                    <span className="shrink-0 text-sm font-semibold text-slate-700">{currencyFormatter.format(Number(order.total))}</span>
                  </button>
                ))}
              </div>
            ) : <p className="px-4 py-8 text-center text-sm text-slate-500">Nenhuma ordem de serviço encontrada para este veículo.</p>}
          </section>
        </div>
      ) : null}
    </AppDialog>
  );
}

function DetailCard({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return <div className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3"><span className="mt-0.5 text-slate-400">{icon}</span><div className="min-w-0"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p><div className="mt-0.5 text-sm font-medium text-slate-800">{children}</div></div></div>;
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div aria-live="assertive" className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"><p>{message}</p><button type="button" onClick={onRetry} className="font-semibold underline underline-offset-2">Tentar novamente</button></div>;
}
